import { Banner, ProgressCircleSize, Spinner } from "@brexhq/component-library";
import { useTheme } from "@emotion/react";
import styled from "@emotion/styled";
import { m, AnimatePresence } from "framer-motion";
import { padding } from "polished";
import * as React from "react";

import { Container } from "@brexhq/next/src/components/layout/Container/Container";
import { Button } from "@brexhq/next/src/components/shared/Button";
import { BynderAsset } from "@brexhq/next/src/components/shared/BynderAsset/BynderAsset";
import { ResponsiveIcon } from "@brexhq/next/src/components/shared/Icon";
import {
  FlechaXS,
  SubtitleS,
} from "@brexhq/shared/components/Typography/Components";

import { DynamicLeadFormController } from "./dynamicLeadFormController";
import {
  BACK_BUTTON_TEST_ID,
  ERROR_BOX_MESSAGE,
  FormSubmissionState,
  SUBMIT_BUTTON_TEST_ID,
} from "./types";

export const actionsContainerHeight = 96;

export const Wrapper = styled.div<{ height: string }>(({ theme, height }) => ({
  display: "grid",
  height,
  gridTemplateColumns: "1fr 1fr",
  [theme.breakpoints.MED]: {
    gridTemplateColumns: "1fr",
    height: "100%",
  },
  [theme.breakpoints.SML]: {
    gridTemplateColumns: "1fr",
    height: "100%",
  },
}));

export const Image = styled(BynderAsset)({
  objectFit: "cover",
  position: "absolute",
  width: "100%",
  height: "100%",
  inset: 0,
  zIndex: -1,
});

export const ImageContainer = styled.div(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  height: "100%",
  [theme.breakpoints.MED]: {
    display: "none",
  },

  [theme.breakpoints.SML]: {
    display: "none",
  },
}));

export const TextContainer = styled.div({
  position: "absolute",
  top: 0,
  zIndex: 1,
  width: "100%",
  height: "100%",
  textAlign: "left",
  display: "flex",
  flexDirection: "column",
  justifyContent: "space-between",
});

export const FormContainer = styled.div(({ theme }) => ({
  display: "grid",
  height: "100%",
  [theme.breakpoints.SML]: {
    gridTemplateRows: "1fr",
  },
}));

export const ActionsContainer = styled.div(({ theme }) => ({
  borderTop: `1px solid ${theme.colors.border.default}`,
}));

export const Scrollable = styled.div<{ height: string }>(({ height }) => ({
  height,
  overflowY: "scroll",
  // remove scrollbar FF
  scrollbarWidth: "none",
  // remove scrollbar chromium
  "&::-webkit-scrollbar": {
    width: "0!important",
    display: "none",
  },
}));

export const AnimationGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "1fr",
  width: "100%",
});

export const AnimatingGridItem = styled(m.div)(() => ({
  gridColumn: 1,
  gridRow: 1,
}));
export const ErrorBox: React.FC<{
  onSubmit: (
    e?: React.BaseSyntheticEvent<object, any, any> | undefined,
  ) => Promise<void>;
}> = ({ onSubmit }) => {
  return (
    <Container margin={["sml", "none", "none", "none"]}>
      <Banner
        title="Error"
        variant="error"
        onClickCta={onSubmit}
        cta="Try Again"
      >
        {ERROR_BOX_MESSAGE}
      </Banner>
    </Container>
  );
};

export const LogoGrid = styled.div(({ theme }) => {
  return {
    display: "grid",
    gridTemplateColumns: "1fr 1fr 1fr 1fr",
    gridTemplateRows: "1fr 1fr",
    rowGap: theme.spacing.LRG.med,
    columnGap: theme.spacing.LRG.lrg,
    backgroundColor: theme.colors.content.primary,
    "@media screen and (max-height: 799px)": {
      display: "none",
    },
  };
});

export const Divider = styled.hr(({ theme }) => ({
  marginTop: theme.spacing.LRG.sml,
  marginBottom: theme.spacing.LRG.sml,
  padding: 0,
  borderBottom: `1px solid ${theme.colors.content.inverse.primary}`,
}));

export const QuoteContainer = styled.div(({ theme }) => ({
  flex: 1,
  backgroundColor: theme.colors.content.primary,
  ...padding(
    60,
    theme.spacing.LRG.xlrg,
    theme.spacing.LRG.xlrg,
    theme.spacing.LRG.xlrg,
  ),
}));

export const FlexChild = styled.div({
  flex: 1,
});

const LogoGridTopRow = styled.div(({ theme }) => ({
  display: "grid",
  gridAutoColumns: "minmax(85px, 1fr)",
  gridAutoFlow: "column",
  gridColumnStart: 1,
  gridColumnEnd: 5,
  gridRowStart: 1,
  gridRowEnd: 1,
  rowGap: theme.spacing.LRG.sml,
  columnGap: theme.spacing.LRG.lrg,
  maxHeight: theme.spacing.LRG.med,
}));

const LogoGridBottomRow = styled.div(({ theme }) => ({
  display: "grid",
  gridAutoColumns: "minmax(85px, 1fr)",
  gridAutoFlow: "column",
  gridColumnStart: 2,
  gridColumnEnd: 5,
  gridRowStart: 2,
  gridRowEnd: 2,
  rowGap: theme.spacing.LRG.sml,
  columnGap: theme.spacing.LRG.lrg,
  maxHeight: theme.spacing.LRG.med,
}));

export const TopHalfContainer = styled.div(({ theme }) => {
  return {
    flex: 1,
    position: "relative",
    ...padding(
      theme.spacing.LRG.xlrg,
      theme.spacing.LRG.xlrg,
      60,
      theme.spacing.LRG.xlrg,
    ),
  };
});

/**
 * For the contact-sales-quotes experiment
 */
export const ExperimentContent = ({
  quote,
  quotee,
}: {
  quote: string;
  quotee: string;
}) => {
  const { spacing, colors } = useTheme();

  const baseIcon = React.useMemo(
    () => ({
      color: colors.content.primary,
      responsive: {
        LRG: { height: "100%", width: "100%" },
        MED: { height: "100%", width: "100%" },
        SML: { height: "100%", width: "100%" },
      },
    }),
    [colors],
  );

  return (
    <>
      <QuoteContainer>
        <AnimatePresence mode={"wait"}>
          <m.div
            key={quote}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { duration: 0.5 } }}
            exit={{ opacity: 0 }}
            transition={{
              opacity: { duration: 0.2 },
            }}
            style={{
              height: "160px",
            }}
          >
            <FlechaXS color={colors.content.inverse.primary}>{quote}</FlechaXS>
            <SubtitleS
              color={colors.content.inverse.primary}
              margin={{
                LRG: [
                  spacing.LRG.xsml,
                  spacing.LRG.none,
                  spacing.LRG.med,
                  spacing.LRG.none,
                ],
              }}
            >
              {quotee}
            </SubtitleS>
          </m.div>
        </AnimatePresence>
        <Divider />
        <LogoGrid>
          <LogoGridTopRow>
            <SubtitleS color={colors.content.inverse.primary}>
              Trusted by:
            </SubtitleS>
            <ResponsiveIcon {...baseIcon} icon="Doordash" />
            <ResponsiveIcon {...baseIcon} icon="YCombinator" />
            <ResponsiveIcon {...baseIcon} icon="Airbnb" />
          </LogoGridTopRow>
          <LogoGridBottomRow>
            <ResponsiveIcon {...baseIcon} icon="Airtable" />
            <ResponsiveIcon {...baseIcon} icon="Deel" />
            <ResponsiveIcon {...baseIcon} icon="ScaleAi" />
          </LogoGridBottomRow>
        </LogoGrid>
      </QuoteContainer>
    </>
  );
};

export const titleMargin = {
  LRG: [0, 0, 16, 0],
  SML: [0, 0, 16, 0],
};

export const bodyMargin = {
  LRG: [0, 0, 24, 0],
};

export const Actions: React.FC<{
  ctrl: DynamicLeadFormController;
  label?: string;
}> = ({ ctrl, label = "Submit" }) => {
  return (
    <Container justifyContent="flex-end" gap="sml">
      <AnimatePresence initial={false}>
        {ctrl.hasPrevStep && (
          <m.div
            key="back-button"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1, transition: { delay: 2 } }}
            exit={{ opacity: 0 }}
          >
            <Button
              testId={BACK_BUTTON_TEST_ID}
              component="multiStepLeadForm"
              modifier="secondary"
              onClick={ctrl.back}
              label="Back"
              disabled={!ctrl.canDoBack}
              size={{ LRG: "lrg", MED: "lrg", SML: "med" }}
              margin={[0, 0, 0, 0]}
            />
          </m.div>
        )}
      </AnimatePresence>
      <Button
        testId={SUBMIT_BUTTON_TEST_ID}
        component="multiStepLeadForm"
        modifier="primary"
        onClick={ctrl.next}
        label={
          ctrl.formSubmissionState ===
          FormSubmissionState.SUBMISSION_ATTEMPTED ? (
            <Spinner shade="inverse" size={ProgressCircleSize.MEDIUM} />
          ) : (
            label
          )
        }
        disabled={!ctrl.canDoNext}
        size={{ LRG: "lrg", MED: "lrg", SML: "med" }}
        margin={[0, 0, 0, 0]}
      />
    </Container>
  );
};
